import {
  getSuperadminHandoffParamsFromLocation,
  parseImpersonationStateFromRawStateParam,
  stripSuperadminHandoffFromBrowserUrl,
} from '@/helpers/impersonationHandoffUrl';
import { setDataToLs } from '@/helpers/localStorage';
import AuthService from '@/services/auth.service';
import { impersonationService } from '@/services/impersonation.service';
import { LsStoreKey } from '@/types';

export const AUTH_LOGOUT_INTENT_STORAGE_KEY = 'auth-logout-intent';

let superadminHandoffBootstrapped = false;

export const consumeSuperadminHandoffBootstrapped = (): boolean => {
  const wasBootstrapped = superadminHandoffBootstrapped;
  superadminHandoffBootstrapped = false;
  return wasBootstrapped;
};

const hasOidcAuthParamsInLocation = (): boolean => {
  const searchParams = new URLSearchParams(window.location.search);
  if ((searchParams.get('code') || searchParams.get('error')) && searchParams.get('state')) {
    return true;
  }

  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return Boolean((hashParams.get('code') || hashParams.get('error')) && hashParams.get('state'));
};

/**
 * Clears logout intent before OIDC initializes when the URL indicates a new sign-in or
 * impersonation handoff. Otherwise `skipSigninCallback` and `userLoaded` keep rejecting the
 * session left over from "End impersonation" (same tab, sessionStorage still set).
 */
export const clearLogoutIntentIfStartingSignInOrImpersonation = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!AuthService.hasLogoutIntent()) {
    return;
  }

  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));

  if (searchParams.get('code')?.trim() || hashParams.get('code')?.trim()) {
    AuthService.clearLogoutIntent();
    return;
  }

  if (searchParams.get('X-Impersonate-User')?.trim()) {
    AuthService.clearLogoutIntent();
    return;
  }

  if (searchParams.get('impersonatedUserName')?.trim() || searchParams.get('returnToControlCenterUrl')?.trim()) {
    AuthService.clearLogoutIntent();
    return;
  }

  const { accessToken, rawState } = getSuperadminHandoffParamsFromLocation({
    search: window.location.search,
    hash: window.location.hash,
  });

  if (accessToken?.trim() && rawState?.trim()) {
    AuthService.clearLogoutIntent();
    return;
  }

  if (rawState?.trim()) {
    const impersonationFromState = parseImpersonationStateFromRawStateParam(rawState);
    if (impersonationFromState) {
      AuthService.clearLogoutIntent();
    }
  }
};

/**
 * Runs before React so react-oidc-context does not complete signinCallback while the user is
 * returning from Cognito logout (logout_uri may match redirect_uri, so the URL can still carry
 * OAuth parameters). Stripping the query/hash synchronously avoids a race with OIDC init.
 */
export const stripOAuthParamsIfPendingLogoutReturn = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const hasLogoutIntent = window.sessionStorage.getItem(AUTH_LOGOUT_INTENT_STORAGE_KEY) === '1';

  if (!hasLogoutIntent) {
    return;
  }

  if (!window.location.pathname.endsWith('/callback')) {
    return;
  }

  if (!hasOidcAuthParamsInLocation()) {
    return;
  }

  window.history.replaceState({}, document.title, window.location.pathname);
};

/**
 * Seeds the OIDC user blob in sessionStorage from a superadmin impersonation handoff URL
 * before react-oidc-context initializes. Without this, getUser() runs with no stored user
 * and token refresh never arms because only access_token was written to localStorage.
 */
export const seedSuperadminHandoffOidcSessionFromUrl = async (): Promise<boolean> => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (AuthService.hasLogoutIntent()) {
    return false;
  }

  const { accessToken, idToken, refreshToken, expiresIn, tokenType, scope, rawState } =
    getSuperadminHandoffParamsFromLocation({
      search: window.location.search,
      hash: window.location.hash,
    });

  if (!accessToken?.trim() || !rawState?.trim()) {
    return false;
  }

  const impersonationState = parseImpersonationStateFromRawStateParam(rawState);
  if (!impersonationState) {
    return false;
  }

  if (AuthService.hasOidcUserInSessionStorage()) {
    impersonationService.applyFromOidcState({ state: impersonationState });
    return true;
  }

  await AuthService.applyImpersonationHandoff({
    accessToken,
    idToken,
    refreshToken,
    expiresIn,
    tokenType,
    scope,
  });
  impersonationService.applyFromOidcState({ state: impersonationState });

  const churchIdFromQuery = new URLSearchParams(window.location.search).get('church_id')?.trim() || null;
  const churchIdFromHandoff = impersonationState.church_id?.trim() || null;
  const churchId = churchIdFromHandoff || churchIdFromQuery;
  if (churchId) {
    setDataToLs(LsStoreKey.LoginChurchId, churchId);
  }

  stripSuperadminHandoffFromBrowserUrl({ churchId });
  superadminHandoffBootstrapped = true;
  return true;
};
